import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Users, Search, Eye, Star } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const StarRating = ({ rating }) => {
  const value = parseFloat(rating) || 0;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${
            star <= Math.round(value)
              ? 'text-yellow-400 fill-yellow-400'
              : 'text-gray-300 fill-gray-100'
          }`}
        />
      ))}
    </div>
  );
};

const DriverStats = () => {
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [regionalOfficeFilter, setRegionalOfficeFilter] = useState('');
  const [developerOnly, setDeveloperOnly] = useState('Exclude');
  const [liveStatus, setLiveStatus] = useState('');
  const [isApproved, setIsApproved] = useState('true');
  const [isBlocked, setIsBlocked] = useState('');
  const [isBankVerified, setIsBankVerified] = useState('');
  const [incompleteDetails, setIncompleteDetails] = useState('');
  const [regionalOffices, setRegionalOffices] = useState([]);
  const limit = 10;

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const res = await api.get('/regional-offices', { params: { limit: 100 } });
        setRegionalOffices(res?.data?.data?.offices || []);
      } catch (e) {
        console.error('Failed to fetch regional offices:', e);
      }
    };
    fetchOffices();
  }, []);

  const { data, isLoading, error } = useQuery(
    ['driver-stats-list', page, search, regionalOfficeFilter, developerOnly, liveStatus, isApproved, isBlocked, isBankVerified, incompleteDetails],
    async () => {
      const params = { page, limit };
      if (search) params.search = search;
      if (regionalOfficeFilter) params.regionalOfficeFilter = regionalOfficeFilter;
      if (developerOnly) params.developerOnly = developerOnly;
      if (isApproved !== '') params.isApproved = isApproved;
      if (isBlocked !== '') params.isBlocked = isBlocked;
      if (isBankVerified !== '') params.isBankVerified = isBankVerified;
      if (incompleteDetails !== '') params.incompleteDetails = incompleteDetails;

      if (liveStatus) {
        params.uiStatus = liveStatus;
        if (
          liveStatus === "notavailable" ||
          liveStatus === "active" ||
          liveStatus === "available_online"
        ) {
          params.status = "online";
          if (liveStatus === "notavailable") {
            params.isAvailable = "false";
          } else if (liveStatus === "active") {
            params.isAvailable = "true";
          }
        } else {
          params.status = liveStatus;
        }
      }

      return await api.get('/drivers', { params });
    },
    { keepPreviousData: true, refetchOnMount: true }
  );

  useEffect(() => {
    if (error) toast.error('Failed to load drivers');
  }, [error, toast]);

  const drivers = data?.data?.data?.drivers || [];
  const pagination = data?.data?.data?.pagination || {};
  const hasActiveFilters = search || regionalOfficeFilter || developerOnly !== 'Exclude' || liveStatus || isApproved !== 'true' || isBlocked || isBankVerified || incompleteDetails;

  const clearFilters = () => {
    setSearch('');
    setRegionalOfficeFilter('');
    setDeveloperOnly('Exclude');
    setLiveStatus('');
    setIsApproved('true');
    setIsBlocked('');
    setIsBankVerified('');
    setIncompleteDetails('');
    setPage(1);
  };

  const getStatusBadge = (driver) => {
    if (driver.isBlocked) {
      return <span className="badge badge-danger">Blocked</span>;
    }
    if (!driver.isApproved) {
      return <span className="badge badge-warning">Pending</span>;
    }

    // Check if driver is in a trip based on trip status
    if (driver.currentTripId && driver.currentTripStatus) {
      const tripStatus = driver.currentTripStatus;

      // Show "In Trip" for ACCEPTED and PICKEDUP statuses
      if (tripStatus === "ACCEPTED" || tripStatus === "PICKEDUP") {
        const tripStatusLabel =
          tripStatus === "PICKEDUP" ? "Picked Up" : "Accepted";
        const tripStatusBadgeClass =
          tripStatus === "ACCEPTED" ? "badge-primary" : "badge-info";
        return (
          <div className="flex flex-col items-start space-y-1">
            <div className="flex items-center gap-2">
              <span className="badge badge-info">In Trip</span>
              <span className={`badge ${tripStatusBadgeClass}`}>
                {tripStatusLabel}
              </span>
            </div>
            {hasPermission("trips", "view") && (
              <Link
                to={`/trips/${driver.currentTripId}`}
                className="text-xs text-primary-600 hover:text-primary-800 underline"
              >
                View Trip
              </Link>
            )}
          </div>
        );
      }
      // Show "Last Trip" for other completed statuses
      if (
        tripStatus === "DROPPED" ||
        tripStatus === "PAYMENT_COMPLETED" ||
        tripStatus === "COMPLETED"
      ) {
        return (
          <div className="flex flex-col items-start space-y-1">
            {driver.driverStatus?.status?.toLowerCase() === "online" &&
              driver.isAvailable ? (
              <span className="badge badge-success">Online</span>
            ) : driver.driverStatus?.status?.toLowerCase() === "online" &&
              !driver.isAvailable ? (
              <span className="badge badge-warning">
                Online - Not Available
              </span>
            ) : (
              <span className="badge badge-danger">Offline</span>
            )}
            <span className="badge badge-info">Last Trip</span>
            {hasPermission("trips", "view") && (
              <Link
                to={`/trips/${driver.currentTripId}`}
                className="text-xs text-primary-600 hover:text-primary-800 underline"
              >
                View Last Trip
              </Link>
            )}
          </div>
        );
      }
    }

    if (driver.driverStatus?.status?.toLowerCase() === "offline") {
      return <span className="badge badge-info">Offline</span>;
    }
    if (
      driver.driverStatus?.status?.toLowerCase() === "online" &&
      !driver.isAvailable
    ) {
      return (
        <span className="badge badge-warning">Online - Not Available</span>
      );
    }
    return <span className="badge badge-success">Online</span>;
  };

  const labels = {
    pageTitle: 'Driver Stats',
    pageSubtitle: 'View all approved drivers and their statistics',
    filters: 'Filters',
    searchDriver: 'Search Driver',
    searchPlaceholder: 'Search by name or phone...',
    regionalOffice: 'Regional Office',
    allOffices: 'All Offices',
    developerFilter: 'Developer Filter',
    includeAll: 'Include All Drivers',
    excludeDev: 'Exclude Developers',
    devOnly: 'Developers Only',
    clearFilters: 'Clear Filters',
    noDrivers: 'No drivers found',
    noDriversFiltered: 'No drivers match your current filters.',
    noDriversDefault: 'No approved drivers available.',
    colName: 'Driver Name',
    colPhone: 'Driver Phone Number',
    colVehicle: 'Vehicle Details',
    colStatus: 'Driver Status',
    colGender: 'Gender',
    colRatings: 'Driver Ratings',
    colAction: 'Action',
    noVehicle: 'No Vehicle',
    noRegNo: 'No Reg No',
    reviews: 'reviews',
    viewDetails: 'View Details',
    previous: 'Previous',
    next: 'Next',
    liveStatus: 'Live Status',
    allStatuses: 'All Statuses',
    online: 'Online',
    inTrip: 'In Trip',
    onlineActive: 'Online & Active',
    onlineAvailable: 'Online & Available',
    onlineNotAvailable: 'Online - Not Available',
    offline: 'Offline',
    approvalStatus: 'Approval Status',
    allApprovals: 'All Approvals',
    approved: 'Approved',
    pending: 'Pending',
    bankStatus: 'Bank Approved Status',
    allBankStatuses: 'All',
    bankApprovedOption: 'Approved',
    bankPendingOption: 'Pending',
    blockedFilter: 'Blocked Status',
    allBlocked: 'All',
    blocked: 'Blocked',
    activeOnly: 'Active',
    profileCompletion: 'Profile Completion',
    allDrivers: 'All Drivers',
    completeProfile: 'Complete Profile & Vehicle Docs',
    incompleteProfile: 'Missing Profile or Vehicle Docs',
  };

  return (
    <div className="space-y-6">

      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <h1 className="page-title">{labels.pageTitle}</h1>
          <p className="page-subtitle">{labels.pageSubtitle}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{labels.filters}</h3>
            <button onClick={clearFilters} className="btn btn-outline btn-sm">
              {labels.clearFilters}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

            <div>
              <label htmlFor="search-driver" className="form-label">{labels.searchDriver}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="search-driver"
                  type="text"
                  placeholder={labels.searchPlaceholder}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="input pl-10"
                />
              </div>
            </div>

            <div>
              <label htmlFor="regional-office" className="form-label">{labels.regionalOffice}</label>
              <select
                id="regional-office"
                value={regionalOfficeFilter}
                onChange={(e) => { setRegionalOfficeFilter(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="">{labels.allOffices}</option>
                {regionalOffices.map((office) => (
                  <option key={office._id} value={office._id}>{office.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="live-status" className="form-label">{labels.liveStatus}</label>
              <select
                id="live-status"
                value={liveStatus}
                onChange={(e) => { setLiveStatus(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="">{labels.allStatuses}</option>
                <option value="online">{labels.online}</option>
                <option value="busy">{labels.inTrip}</option>
                <option value="active">{labels.onlineActive}</option>
                <option value="available_online">{labels.onlineAvailable}</option>
                <option value="notavailable">{labels.onlineNotAvailable}</option>
                <option value="offline">{labels.offline}</option>
              </select>
            </div>

            <div>
              <label htmlFor="approval-status" className="form-label">{labels.approvalStatus}</label>
              <select
                id="approval-status"
                value={isApproved}
                onChange={(e) => { setIsApproved(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="">{labels.allApprovals}</option>
                <option value="true">{labels.approved}</option>
                <option value="false">{labels.pending}</option>
              </select>
            </div>

            <div>
              <label htmlFor="bank-status" className="form-label">{labels.bankStatus}</label>
              <select
                id="bank-status"
                value={isBankVerified}
                onChange={(e) => { setIsBankVerified(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="">{labels.allBankStatuses}</option>
                <option value="true">{labels.bankApprovedOption}</option>
                <option value="false">{labels.bankPendingOption}</option>
              </select>
            </div>

            <div>
              <label htmlFor="blocked-filter" className="form-label">{labels.blockedFilter}</label>
              <select
                id="blocked-filter"
                value={isBlocked}
                onChange={(e) => { setIsBlocked(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="">{labels.allBlocked}</option>
                <option value="true">{labels.blocked}</option>
                <option value="false">{labels.activeOnly}</option>
              </select>
            </div>

            <div>
              <label htmlFor="developer-filter" className="form-label">{labels.developerFilter}</label>
              <select
                id="developer-filter"
                value={developerOnly}
                onChange={(e) => { setDeveloperOnly(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="Include">{labels.includeAll}</option>
                <option value="Exclude">{labels.excludeDev}</option>
                <option value="Only">{labels.devOnly}</option>
              </select>
            </div>

            <div>
              <label htmlFor="profile-completion" className="form-label">{labels.profileCompletion}</label>
              <select
                id="profile-completion"
                value={incompleteDetails}
                onChange={(e) => { setIncompleteDetails(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="">{labels.allDrivers}</option>
                <option value="complete">{labels.completeProfile}</option>
                <option value="incomplete">{labels.incompleteProfile}</option>
              </select>
            </div>

          </div>
        </div>

        {/* Table Header */}
        <div className="card-header border-t">
          <h3 className="text-lg font-medium text-gray-900">
            Drivers ({pagination.totalRecords || 0})
          </h3>
        </div>

        {/* Table Body */}
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : drivers.length === 0 ? (
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">{labels.noDrivers}</h3>
              <p className="empty-state-description">
                {hasActiveFilters ? labels.noDriversFiltered : labels.noDriversDefault}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">{labels.colName}</th>
                    <th className="table-header-cell">{labels.colPhone}</th>
                    <th className="table-header-cell">{labels.colVehicle}</th>
                    <th className="table-header-cell">{labels.colStatus}</th>
                    <th className="table-header-cell">{labels.colGender}</th>
                    <th className="table-header-cell">{labels.colRatings}</th>
                    <th className="table-header-cell">{labels.colAction}</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {drivers.map((driver) => (
                    <tr key={driver._id} className="table-row">

                      {/* Driver Name */}
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-primary-700">
                              {driver.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{driver.name || ''}</div>
                            <div className="text-xs text-gray-500">{driver.licenseNo || ''}</div>
                          </div>
                        </div>
                      </td>

                      {/* Driver Phone Number */}
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{driver.phone || ''}</div>
                      </td>

                      {/* Vehicle Details */}
                      <td className="table-cell">
                        {driver.vehicleId ? (
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium text-gray-900">
                              {driver.vehicleId.regNo || labels.noRegNo}
                            </div>
                            <div className="text-xs text-gray-500">
                              {driver.vehicleId.model}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">{labels.noVehicle}</span>
                        )}
                      </td>

                      {/* Driver Status */}
                      <td className="table-cell">
                        {getStatusBadge(driver)}
                      </td>

                      {/* Gender */}
                      <td className="table-cell">
                        <span className="text-sm text-gray-900 capitalize">
                          {driver.gender || 'N/A'}
                        </span>
                      </td>

                      {/* Driver Ratings */}
                      <td className="table-cell">
                        <div className="space-y-1">
                          <StarRating rating={driver.ratingData?.currentrating} />
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-gray-900">
                              {typeof driver.ratingData?.currentrating === 'number'
                                ? driver.ratingData.currentrating.toFixed(1)
                                : '0.0'}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({driver.ratingData?.count || 0} {labels.reviews})
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="table-cell">
                        <Link
                          to={`/drivers/${driver._id}/statistics`}
                          className="text-primary-600 hover:text-primary-900"
                          title={labels.viewDetails}
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalRecords > 0 && pagination.total > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.current * pagination.limit, pagination.totalRecords)} of{' '}
            {pagination.totalRecords} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={pagination.current === 1}
              className="btn btn-outline btn-sm"
            >
              {labels.previous}
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.current === pagination.total}
              className="btn btn-outline btn-sm"
            >
              {labels.next}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default DriverStats;